import { useMemo } from 'react';
import { useStoreActions, useStoreStatic } from "../store/store-context";
import { selectors } from "../store/state-selectors";
import { ServiceTemplateType } from "../models/service-template";
import { useOverlayContext } from 'seagull';
import { ErrorResponse, ServerError } from "../infra/ErrorResponse";
import { useFeatureFlags } from "./useFeatureFlags";

/** Returns functions that make requests to the back-end for service-template CRUD API. */
export const useServiceTemplateApi = () => {
  const {
    showErrorToast
  } = useOverlayContext();
  const {
    getState
  } = useStoreStatic();
  const {
    api,
    updateBusinessClientData
  } = useStoreActions();
  const {
    FIX_MAP_SERVICES_IN_INVOICE
  } = useFeatureFlags();
  return useMemo(() => {
    const getBusinessId = () => selectors.user.businessId(getState());
    const handleServiceTemplateExistsError = (err, serviceTemplateName) => {
      if (new ErrorResponse(err).hasServerError(ServerError.ServiceTemplateAlreadyExists)) {
        showErrorToast("A service with the name ".concat(serviceTemplateName, " already exists"));
      }
      throw err;
    };
    async function updateModifiedAServiceTemplate(value) {
      const bid = getBusinessId();
      const {
        modifiedAServiceTemplate
      } = selectors.user.businessClientData(getState(), bid) || {};
      if (bid && !modifiedAServiceTemplate) {
        await updateBusinessClientData(bid, {
          modifiedAServiceTemplate: true
        });
      }
      return value;
    }
    function get(opts) {
      return api.serviceTemplate.getAll(getBusinessId(), opts);
    }
    return {
      getActive: async () => get({
        includeDeleted: false,
        includeAdhocCharges: false,
        type: [ServiceTemplateType.Service, ServiceTemplateType.Bundle]
      }).then(r => r.active),
      getForBilling: async () => get({
        includeDeleted: false,
        includeAdhocCharges: true,
        type: [ServiceTemplateType.Service, ServiceTemplateType.Bundle]
      }).then(r => r.raw),
      getForMappingToAccountingIntegration: async () => get({
        includeDeleted: true,
        includeAdhocCharges: true,
        type: [ServiceTemplateType.Service, ServiceTemplateType.Bundle]
      }).then(r => r.raw),
      getForMappingToPracticeManagementIntegration: async () => get({
        includeDeleted: true,
        includeAdhocCharges: true,
        type: [ServiceTemplateType.Service]
      }).then(r => r.raw),
      getForInvoice: invoiceId => get({
        invoiceId,
        type: [ServiceTemplateType.Service, ServiceTemplateType.Bundle]
      }).then(r => FIX_MAP_SERVICES_IN_INVOICE ? r.raw : r.active),
      getActiveServices: async () => get({
        includeDeleted: false,
        includeAdhocCharges: false,
        type: [ServiceTemplateType.Service]
      }).then(r => r.active),
      create(serviceTemplate) {
        return api.serviceTemplate.create(serviceTemplate, getBusinessId()).catch(err => handleServiceTemplateExistsError(err, serviceTemplate.name)).then(updateModifiedAServiceTemplate);
      },
      update(serviceTemplate, descriptionObj) {
        return api.serviceTemplate.update(serviceTemplate, getBusinessId(), descriptionObj).catch(err => handleServiceTemplateExistsError(err, serviceTemplate.name)).then(updateModifiedAServiceTemplate);
      },
      delete(id) {
        return api.serviceTemplate.delete(id, getBusinessId());
      }
    };
  }, []);
};